import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/common";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Workspace from "@/pages/Workspace";
import Dashboard from "@/pages/Dashboard";
import AiCeo from "@/pages/AiCeo";
import Briefing from "@/pages/Briefing";
import BriefingHistory from "@/pages/BriefingHistory";
import CalendarPage from "@/pages/CalendarPage";
import People from "@/pages/People";
import Leave from "@/pages/Leave";
import Onboarding from "@/pages/Onboarding";
import Tasks from "@/pages/Tasks";
import Goals from "@/pages/Goals";
import Approvals from "@/pages/Approvals";
import Settings from "@/pages/Settings";
import Cfo from "@/pages/Cfo";
import Marketing from "@/pages/Marketing";
import Seo from "@/pages/Seo";
import Admin from "@/pages/Admin";
import Availability from "@/pages/Availability";
import PublicBooking from "@/pages/PublicBooking";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Pricing from "@/pages/Pricing";

function RequireAuth({ children, needCompany = true }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (needCompany && !user.company_id) return <Navigate to="/workspace" replace />;
  return children;
}

function RequirePlatformAdmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!user.is_platform_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoader />;
  if (user) return <Navigate to={user.company_id ? "/dashboard" : "/workspace"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Login initialMode="signup" /></PublicOnly>} />
      <Route path="/workspace" element={<RequireAuth needCompany={false}><Workspace /></RequireAuth>} />
      <Route path="/payment/success" element={<RequireAuth needCompany={false}><PaymentSuccess /></RequireAuth>} />
      <Route path="/payment/cancel" element={<RequireAuth needCompany={false}><PaymentCancel /></RequireAuth>} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/ai-ceo" element={<AiCeo />} />
        <Route path="/briefing" element={<Briefing />} />
        <Route path="/briefing/history" element={<BriefingHistory />} />
        <Route path="/cfo" element={<Cfo />} />
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/seo" element={<Seo />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/availability" element={<Availability />} />
        <Route path="/people" element={<People />} />
        <Route path="/leave" element={<Leave />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<RequirePlatformAdmin><Admin /></RequirePlatformAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
